import type { DatasetIndex, ProjectConfigs } from "@/types";

/**
 * The tenant's filter model, normalised.
 *
 * Its source is `config.datasetsLayers[].indexes`, authored in the CMS. Two
 * things about the stored shape are historical rather than meaningful, and are
 * cleaned up here so nothing downstream has to know about them:
 *
 *  - `field` is usually written `properties.<name>`, because the legacy backend
 *    stored a feature as `{ properties: { … } }` and the index addressed the
 *    whole document. Our API takes a property name.
 *  - a translated field is sometimes written `<name>::fr`, from when translations
 *    were flattened into one column per language at import time. Language is a
 *    request parameter now.
 */

/** Filter behaviours the UI can render. `fulltext` drives search, not a control. */
export type FilterKind = "simple" | "range" | "daterange";

export interface FilterDef {
  /** Identity: the URL key, the translation key, and the facet's label. */
  name: string;
  /** The property to filter and facet on. */
  field: string;
  kind: FilterKind;
  icon?: string;
  unit?: string;
}

/**
 * Indexes the app maintains for itself rather than for the user.
 *
 * `squidex_id` is the import key; `3d`, `3d_models` and `schema_key` drive map
 * layers. The legacy filter list excluded exactly these by name.
 */
const INTERNAL = new Set(["squidex_id", "3d", "3d_models", "schema_key"]);

const KINDS = new Set<string>(["simple", "range", "daterange"]);

/** Strip the legacy `properties.` prefix and `::lang` suffix from a field path. */
export function normalizeField(field: string | null | undefined): string | null {
  if (!field) return null;
  const withoutPrefix = field.replace(/^properties\./, "");
  const withoutLang = withoutPrefix.split("::")[0];
  return withoutLang || null;
}

/** Every index of the project's dataset layers, in authored order. */
function datasetIndexes(configs: ProjectConfigs): DatasetIndex[] {
  return (configs.datasetsLayers ?? []).flatMap((layer) => layer.indexes ?? []);
}

/**
 * The filters to offer, in the order the tenant authored them — or by `orderNum`
 * where they set one, since the CMS list order is not always the display order.
 */
export function filterDefs(configs: ProjectConfigs): FilterDef[] {
  const defs: FilterDef[] = [];

  for (const index of datasetIndexes(configs)) {
    const name = index.name;
    const field = normalizeField(index.field);
    if (!name || !field || INTERNAL.has(name)) continue;
    if (!index.type || !KINDS.has(index.type)) continue;

    defs.push({
      name,
      field,
      kind: index.type as FilterKind,
      icon: index.icon ?? undefined,
      unit: index.unit ?? undefined,
    });
  }

  const ordered = [...defs];
  const orderOf = new Map(
    datasetIndexes(configs)
      .filter((index) => typeof index.orderNum === "number")
      .map((index) => [index.name, index.orderNum as number])
  );
  if (orderOf.size > 0) {
    ordered.sort(
      (a, b) =>
        (orderOf.get(a.name) ?? Number.MAX_SAFE_INTEGER) -
        (orderOf.get(b.name) ?? Number.MAX_SAFE_INTEGER)
    );
  }

  return ordered;
}

/**
 * The properties a project declared searchable.
 *
 * Only their names, and only to answer whether the project has a full-text index
 * at all — which decides whether a term search is worth offering. What is
 * actually searched, and in what order the matches come back, is the backend's:
 * the same declaration reaches it through the collection, weights included, so
 * sending these would only be a chance for the two to disagree.
 *
 * Deduplicated across languages, since `q__fr` and `q__en` name the same
 * properties with different suffixes.
 */
export function searchFields(configs: ProjectConfigs): string[] {
  const fields = new Set<string>();

  for (const index of datasetIndexes(configs)) {
    if (index.type !== "fulltext") continue;
    for (const path of Object.keys(index.fields ?? {})) {
      const field = normalizeField(path);
      if (field) fields.add(field);
    }
  }

  return [...fields];
}
